import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// ── Color tokens ─────────────────────────────────────────────────────────────
const C = {
  text: '#0f172a',
  muted: '#64748b',
  faint: '#94a3b8',
  border: '#e5e7eb',
  bg: '#f8fafc',
  good: '#10b981',
  warn: '#f59e0b',
  bad: '#ef4444',
  brand: '#0e304c',
  accent: '#bb9274',
};

// ── Block 1: Traffic hero (uses GSC topline = matches GSC dashboard) ─────────
function TrafficHero({ stats, days }) {
  if (!stats) return null;

  // Use topline if available (matches GSC dashboard exactly), else fall back to keyword-level
  const topline = stats.topline || {
    clicks: stats.total_clicks || 0,
    impressions: stats.total_impressions || 0,
    ctr: stats.ctr || 0,
  };
  const toplinePrior = stats.topline_prior || stats.prior || {};
  const toplineDelta = stats.topline_delta || stats.delta || {};

  // Trust threshold: only show delta when prior period had meaningful data
  const hasPrior = (toplinePrior.impressions || 0) >= 100;

  const renderTrend = (deltaPct) => {
    if (!hasPrior || deltaPct === null || deltaPct === undefined) {
      return <span style={{ fontSize: 11, color: C.faint }}>vs. prior period: not enough data</span>;
    }
    const positive = deltaPct > 0;
    const negative = deltaPct < 0;
    const color = positive ? C.good : (negative ? C.bad : C.muted);
    const arrow = positive ? '↑' : (negative ? '↓' : '·');
    const sign = positive ? '+' : '';
    return (
      <span style={{ fontSize: 11, color }}>
        {arrow} {sign}{deltaPct}% vs. prior {days} days
      </span>
    );
  };

  const ctrDelta = toplineDelta.ctr_pct_points;
  const renderCtrTrend = () => {
    if (!hasPrior || ctrDelta === null || ctrDelta === undefined) {
      return <span style={{ fontSize: 11, color: C.faint }}>vs. prior period: not enough data</span>;
    }
    const color = ctrDelta >= 0 ? C.good : C.bad;
    const arrow = ctrDelta >= 0 ? '↑' : '↓';
    return (
      <span style={{ fontSize: 11, color }}>
        {arrow} {Math.abs(ctrDelta).toFixed(2)}pp vs. prior
      </span>
    );
  };

  const anonClicks = stats.anonymized?.clicks || 0;
  const anonPct = stats.anonymized?.pct || 0;

  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 14 }}>
        Your Google traffic — last {days} days
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 24, alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontSize: 11, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Clicks</div>
          <div style={{ fontSize: 44, fontWeight: 700, color: C.text, lineHeight: 1, letterSpacing: '-0.02em' }}>
            {(topline.clicks || 0).toLocaleString()}
          </div>
          <div style={{ marginTop: 6 }}>{renderTrend(toplineDelta.clicks_pct)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Impressions</div>
          <div style={{ fontSize: 30, fontWeight: 700, color: C.text, lineHeight: 1.1 }}>
            {(topline.impressions || 0).toLocaleString()}
          </div>
          <div style={{ marginTop: 6 }}>{renderTrend(toplineDelta.impressions_pct)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Click rate</div>
          <div style={{ fontSize: 30, fontWeight: 700, color: C.text, lineHeight: 1.1 }}>
            {(topline.ctr || 0).toFixed(2)}%
          </div>
          <div style={{ marginTop: 6 }}>{renderCtrTrend()}</div>
        </div>
      </div>

      <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.border}`, fontSize: 13, color: C.muted, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <strong style={{ color: C.text }}>{stats.keywords_with_clicks || 0} keywords</strong> drove{' '}
          <strong style={{ color: C.text }}>{(stats.total_clicks || 0).toLocaleString()}</strong> of those clicks
          {anonClicks > 0 && (
            <>
              {' · '}
              <span title="Google anonymizes queries with very few clicks for privacy. These clicks are real but cannot be broken out by keyword.">
                <strong style={{ color: C.text }}>{anonClicks.toLocaleString()}</strong> clicks ({anonPct}%) from queries Google won't reveal
              </span>
            </>
          )}
        </div>
        {hasPrior && toplineDelta.clicks_pct !== null && toplineDelta.clicks_pct !== undefined && (
          <div style={{ fontSize: 11, color: C.faint }}>
            Prior period: {(toplinePrior.clicks || 0).toLocaleString()} clicks · {(toplinePrior.impressions || 0).toLocaleString()} impr
          </div>
        )}
      </div>
    </div>
  );
}

// ── Block 2: Top keywords by clicks ───────────────────────────────────────────
function TopByClicks({ keywords, onJumpToKeywords, onViewSerp }) {
  const winners = (keywords || [])
    .filter(k => (k.total_clicks || 0) > 0)
    .sort((a, b) => (b.total_clicks || 0) - (a.total_clicks || 0))
    .slice(0, 5);

  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
            Driving real traffic
          </div>
          <div style={{ fontSize: 12, color: C.faint, marginTop: 2 }}>
            Top 5 keywords actually getting clicks
          </div>
        </div>
        <button onClick={onJumpToKeywords}
          style={{ fontSize: 12, padding: '6px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', color: C.muted, cursor: 'pointer', fontWeight: 500 }}>
          See all →
        </button>
      </div>

      {winners.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px 0', color: C.faint, fontSize: 13 }}>
          No keywords got clicks in this period. Look at "Lost opportunities" — those have impressions but no clicks.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {winners.map((k, i) => {
            const pos = k.avg_position || 0;
            const posColor = pos <= 3 ? C.good : pos <= 10 ? '#3b82f6' : C.warn;
            return (
              <div key={i} onClick={() => onViewSerp && onViewSerp(k.keyword)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: C.bg, borderRadius: 8, cursor: 'pointer', transition: 'background 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={e => e.currentTarget.style.background = C.bg}>
                <div style={{ minWidth: 38, height: 26, borderRadius: 6, background: '#fff', border: `1px solid ${C.border}`, color: posColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11 }}>
                  #{pos.toFixed(1)}
                </div>
                <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 500, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {k.keyword}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{(k.total_clicks || 0).toLocaleString()} clicks</div>
                  <div style={{ fontSize: 11, color: C.faint }}>{(k.total_impressions || 0).toLocaleString()} impr</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Block 3: Lost opportunities ──────────────────────────────────────────────
function LostOpportunities({ keywords, onJumpToKeywords, onViewSerp }) {
  const lost = (keywords || [])
    .filter(k => (k.total_impressions || 0) >= 10 && (k.total_clicks || 0) === 0)
    .sort((a, b) => (b.total_impressions || 0) - (a.total_impressions || 0))
    .slice(0, 5);

  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
            Lost opportunities
          </div>
          <div style={{ fontSize: 12, color: C.faint, marginTop: 2 }}>
            Showing in Google but nobody clicks
          </div>
        </div>
        <button onClick={onJumpToKeywords}
          style={{ fontSize: 12, padding: '6px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', color: C.muted, cursor: 'pointer', fontWeight: 500 }}>
          See all →
        </button>
      </div>

      {lost.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px 0', color: C.good, fontSize: 13, fontWeight: 500 }}>
          ✓ Every keyword with impressions is converting to clicks
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {lost.map((k, i) => {
            const pos = k.avg_position || 0;
            let actionLabel, actionColor;
            if (pos <= 3) {
              actionLabel = 'Fix title or meta';
              actionColor = C.warn;
            } else if (pos <= 10) {
              actionLabel = 'Improve CTR';
              actionColor = C.warn;
            } else if (pos <= 20) {
              actionLabel = 'Push to page 1';
              actionColor = '#3b82f6';
            } else {
              actionLabel = 'Rank higher';
              actionColor = C.bad;
            }
            return (
              <div key={i} onClick={() => onViewSerp && onViewSerp(k.keyword)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: C.bg, borderRadius: 8, cursor: 'pointer', transition: 'background 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={e => e.currentTarget.style.background = C.bg}>
                <div style={{ minWidth: 38, height: 26, borderRadius: 6, background: '#fff', border: `1px solid ${C.border}`, color: C.muted, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11 }}>
                  #{pos.toFixed(1)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {k.keyword}
                  </div>
                  <div style={{ fontSize: 11, color: actionColor, fontWeight: 600, marginTop: 1 }}>
                    {actionLabel}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{(k.total_impressions || 0).toLocaleString()}</div>
                  <div style={{ fontSize: 11, color: C.faint }}>impressions, 0 clicks</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Block 4: Ranking distribution (demoted) ──────────────────────────────────
const DIST_BUCKETS = [
  { key: 'top3',   label: 'Top 3',  color: C.good,    desc: '1\u20133' },
  { key: 'page1',  label: 'Page 1', color: '#3b82f6', desc: '4\u201310' },
  { key: 'page2',  label: 'Page 2', color: C.warn,    desc: '11\u201320' },
  { key: 'page3',  label: 'Page 3', color: '#f97316', desc: '21\u201350' },
  { key: 'beyond', label: 'Beyond', color: C.faint,   desc: '51+' },
];

function RankingDistributionCompact({ dist, onJumpToKeywords }) {
  if (!dist) return null;
  const total = dist.total || 1;

  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
            Where you rank
          </div>
          <div style={{ fontSize: 12, color: C.faint, marginTop: 2 }}>
            {total} keywords ranking on Google
          </div>
        </div>
        <button onClick={onJumpToKeywords}
          style={{ fontSize: 12, padding: '6px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', color: C.muted, cursor: 'pointer', fontWeight: 500 }}>
          See all →
        </button>
      </div>

      <div style={{ display: 'flex', height: 28, borderRadius: 6, overflow: 'hidden', gap: 2, background: '#f3f4f6', marginBottom: 12 }}>
        {DIST_BUCKETS.map(b => {
          const data = dist[b.key] || { count: 0, pct: 0 };
          if (data.count === 0) return null;
          return (
            <div key={b.key} title={`${b.label} (${b.desc}): ${data.count} keywords`}
              style={{ width: `${Math.max(data.pct, 4)}%`, background: b.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {data.count >= 3 ? data.count : ''}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11 }}>
        {DIST_BUCKETS.map(b => {
          const data = dist[b.key] || { count: 0 };
          return (
            <div key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: b.color }} />
              <span><strong style={{ color: C.text }}>{data.count}</strong> {b.label.toLowerCase()}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── GA4 coming soon ──────────────────────────────────────────────────────────
function ComingSoonGa4() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      const existing = JSON.parse(localStorage.getItem('ga4_waitlist') || '[]');
      existing.push({ email, ts: new Date().toISOString() });
      localStorage.setItem('ga4_waitlist', JSON.stringify(existing));
    } catch {}
    setSubmitted(true);
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #f5f3ff 0%, #fef3c7 100%)',
      border: '1px solid #ddd6fe', borderRadius: 12, padding: 20, marginBottom: 16,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, flexWrap: 'wrap',
    }}>
      <div style={{ flex: 1, minWidth: 280 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 4 }}>
          Coming Soon
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>
          Google Analytics 4 + Captivar conversion data
        </div>
        <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
          See actual conversions, calls, and form submissions alongside your search traffic.
        </div>
      </div>
      {submitted ? (
        <div style={{ fontSize: 13, color: C.good, fontWeight: 600, padding: '8px 14px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
          ✓ Got you — we will email when ready
        </div>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 8 }}>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="your@email.com" required
            style={{ padding: '10px 14px', fontSize: 13, border: '1.5px solid #e2e8f0', borderRadius: 8, outline: 'none', minWidth: 200 }} />
          <button type="submit"
            style={{ padding: '10px 16px', fontSize: 13, fontWeight: 600, background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
            Notify me
          </button>
        </form>
      )}
    </div>
  );
}

// ── Main Overview ─────────────────────────────────────────────────────────────
function Overview({ websiteId, days = 28 }) {
  const [stats, setStats] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    if (!websiteId) {
      setLoading(false);
      setStats(null);
      setKeywords([]);
      return;
    }
    setLoading(true);
    try {
      const [statsData, kwData] = await Promise.all([
        fetch(`/keyword-intelligence/api/v1/keywords/stats?website_id=${websiteId}&days=${days}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('access_token') || ''}` }
        }).then(r => r.json()),
        api.getKeywords(websiteId, 1, 500, 'total_impressions', days),
      ]);
      setStats(statsData);
      setKeywords(kwData.keywords || []);
    } catch (e) {
      console.error('Overview load error:', e);
    } finally {
      setLoading(false);
    }
  }, [websiteId, days]);

  useEffect(() => { loadData(); }, [loadData]);

  const goToKeywords = () => { window.location.href = '/app/keywords'; };

  if (!websiteId) return (
    <div className="empty-state">
      <div className="empty-icon">🌐</div>
      <h3>No Website Selected</h3>
      <p>Please add a website in Settings to view analytics</p>
    </div>
  );

  if (loading) return (
    <div className="loading">
      <div className="spinner"></div>
      <p>Loading dashboard...</p>
    </div>
  );

  return (
    <div>
      <TrafficHero stats={stats} days={days} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 16, marginBottom: 0 }}>
        <TopByClicks keywords={keywords} onJumpToKeywords={goToKeywords} />
        <LostOpportunities keywords={keywords} onJumpToKeywords={goToKeywords} />
      </div>

      <RankingDistributionCompact dist={stats?.distribution} onJumpToKeywords={goToKeywords} />

      <ComingSoonGa4 />
    </div>
  );
}

export default Overview;
